import { useCallback, useRef } from 'react'

import { useSelector } from 'react-redux'

import { getIsAuth } from 'src/Redux/auth-process/credentialsSlice/selectors'
import { getAllCampaignsCount, useMetadataQuery } from 'src/Redux/metadataApi'
import {
  enteredTrackUrlEvent,
  startedEnterTrackEvent,
  trackAddedEvent,
} from 'src/Helpers/TagManager'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import {
  useTrackAddedMutation,
  usePublicTrackAddedMutation,
} from 'src/Redux/analyticsApi'
import {
  getIsFirstCampaign,
  getUserId,
} from 'src/Redux/auth-process/userSlice/selectors'

interface TrackAddedEventParams {
  trackName: string
  trackDuration: number | string
  trackId: string | number
  artistName: string
  platformType: CampaignPlatformTypeEnum
}

export const usePageViewEvent = (): {
  trackAddedEventHandler: (params: TrackAddedEventParams) => void
  enteredTrackUrlEventHandler: (platform: CampaignPlatformTypeEnum) => void
  startedEnterTrackEventHandler: (platform: CampaignPlatformTypeEnum) => void
} => {
  const [trackAdded] = useTrackAddedMutation()
  const [trackAddedPublic] = usePublicTrackAddedMutation()

  const isEventTrackEnteredSent = useRef(false)
  const isAuth = useSelector(getIsAuth)

  const { data: metadata } = useMetadataQuery(void 0, { skip: !isAuth })

  const amountOfCampaigns = getAllCampaignsCount(metadata)
  const isFirstCampaign = useSelector(getIsFirstCampaign)
  const userId = useSelector(getUserId)

  const campaignNumber = isAuth ? amountOfCampaigns + 1 : 1

  const trackAddedEventHandler = useCallback(
    ({
      trackName,
      trackDuration,
      trackId,
      artistName,
      platformType,
    }: TrackAddedEventParams): void => {
      trackAddedEvent({
        campaignNumber,
        trackName,
        trackDuration,
        trackId,
        artistName,
        isFirstCampaign: Boolean(isFirstCampaign),
        platformName: platformType,
        userId: userId || null,
      })

      isAuth
        ? void trackAdded({
            campaignPlatform: platformType,
            trackId: String(trackId),
            userId: Number(userId || null),
          })
        : void trackAddedPublic({
            campaignPlatform: platformType,
            trackId: String(trackId),
          })
    },
    [
      campaignNumber,
      isAuth,
      isFirstCampaign,
      trackAdded,
      trackAddedPublic,
      userId,
    ],
  )

  const enteredTrackUrlEventHandler = (
    platform: CampaignPlatformTypeEnum,
  ): void => {
    platform === CampaignPlatformTypeEnum.TIKTOK &&
      enteredTrackUrlEvent({
        campaignPlatform: platform,
        userId,
        isFirstCampaign,
        campaignNumber,
      })
  }

  const startedEnterTrackEventHandler = (
    platform: CampaignPlatformTypeEnum,
  ): void => {
    if (
      platform === CampaignPlatformTypeEnum.TIKTOK &&
      !isEventTrackEnteredSent.current
    ) {
      startedEnterTrackEvent({
        campaignPlatform: platform,
        userId,
        isFirstCampaign,
        campaignNumber,
      })
      isEventTrackEnteredSent.current = true
    }
  }

  return {
    trackAddedEventHandler,
    enteredTrackUrlEventHandler,
    startedEnterTrackEventHandler,
  }
}
